import { useMemo, useState } from "react";
import Breadcrumb from "@/components/base/Breadcrumb";
import NewsCard from "@/components/feature/NewsCard";
import { newsCategories, newsList } from "@/mocks/news";
import { useSeo } from "@/hooks/useSeo";

const PAGE_SIZE = 4;

export default function NewsList() {
  useSeo({
    title: "影视资讯 - 挣扎吧亚当君",
    description:
      "影视资讯频道：汇集影视新闻、深度解析与行业动态，为你提供最新的影视行业报道与作品解读。",
    keywords: "影视资讯,影视新闻,行业动态,深度解析,挣扎吧亚当君",
  });

  const [category, setCategory] = useState("全部");
  const [page, setPage] = useState(1);

  const sorted = useMemo(() => [...newsList].sort((a, b) => (a.date < b.date ? 1 : -1)), []);
  const filtered = useMemo(
    () => (category === "全部" ? sorted : sorted.filter((n) => n.category === category)),
    [category, sorted],
  );

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const current = Math.min(page, totalPages);
  const shown = filtered.slice((current - 1) * PAGE_SIZE, current * PAGE_SIZE);

  const changeCategory = (c: string) => {
    setCategory(c);
    setPage(1);
  };

  return (
    <div className="w-full px-4 md:px-8 py-6 md:py-10">
      <Breadcrumb items={[{ label: "首页", to: "/" }, { label: "影视资讯" }]} />

      <div className="mt-6 mb-8">
        <div className="flex items-center gap-2 mb-3">
          <span className="w-6 h-[3px] rounded-full bg-accent-500"></span>
          <span className="text-xs font-label font-medium tracking-[0.2em] text-accent-700 uppercase">
            Newsroom
          </span>
        </div>
        <h1 className="font-heading text-2xl md:text-4xl font-black text-foreground-950">影视资讯</h1>
        <p className="mt-3 text-sm md:text-base text-foreground-600 max-w-2xl">
          影视新闻、深度解析与行业动态，按发布时间由新到旧排列，带你了解作品之外的行业脉络。
        </p>
      </div>

      <div className="flex flex-wrap items-center gap-2 mb-8">
        {["全部", ...newsCategories].map((c) => (
          <button
            key={c}
            type="button"
            onClick={() => changeCategory(c)}
            className={`px-4 py-2 rounded-full text-sm transition-colors whitespace-nowrap cursor-pointer ${
              category === c
                ? "bg-primary-500 text-background-50 font-medium"
                : "bg-background-50 border border-background-200/80 text-foreground-700 hover:border-primary-300"
            }`}
          >
            {c}
          </button>
        ))}
      </div>

      {shown.length === 0 ? (
        <div className="py-20 text-center bg-background-50 rounded-lg border border-dashed border-background-300">
          <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-full bg-secondary-100 text-secondary-500">
            <i className="ri-newspaper-line text-3xl"></i>
          </div>
          <h3 className="font-heading text-lg font-bold text-foreground-950">该分类暂无资讯</h3>
          <p className="mt-2 text-sm text-foreground-500">换个分类看看吧。</p>
        </div>
      ) : (
        <div className="space-y-5">
          {shown.map((n) => (
            <NewsCard key={n.id} item={n} layout="row" />
          ))}
        </div>
      )}

      {totalPages > 1 && (
        <div className="mt-10 flex items-center justify-center gap-2">
          <button
            type="button"
            disabled={current <= 1}
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            className="w-10 h-10 flex items-center justify-center rounded-md border border-background-300 text-foreground-600 disabled:opacity-40 disabled:cursor-not-allowed hover:border-primary-300 cursor-pointer"
            aria-label="上一页"
          >
            <i className="ri-arrow-left-s-line text-lg"></i>
          </button>
          {Array.from({ length: totalPages }).map((_, i) => (
            <button
              key={i}
              type="button"
              onClick={() => setPage(i + 1)}
              className={`w-10 h-10 rounded-md text-sm transition-colors cursor-pointer ${
                current === i + 1
                  ? "bg-primary-500 text-background-50 font-medium"
                  : "border border-background-300 text-foreground-700 hover:border-primary-300"
              }`}
            >
              {i + 1}
            </button>
          ))}
          <button
            type="button"
            disabled={current >= totalPages}
            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            className="w-10 h-10 flex items-center justify-center rounded-md border border-background-300 text-foreground-600 disabled:opacity-40 disabled:cursor-not-allowed hover:border-primary-300 cursor-pointer"
            aria-label="下一页"
          >
            <i className="ri-arrow-right-s-line text-lg"></i>
          </button>
        </div>
      )}
    </div>
  );
}